/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useSelector } from 'react-redux';
import type { TimeSeriesService } from '@opengeoweb/shared';
import { uiSelectors, uiTypes, useSetupDialog } from '@opengeoweb/store';
import { ParameterInfoDialog } from './ParameterInfoDialog';
import { timeSeriesSelectors } from '../../../store';
import type { TimeSeriesRootState } from '../../../store/types';
import {
  useGetDetailsForCollection,
  useGetInstances,
} from '../../../utils/timeseries-api/hooks';

export const ParameterInfoDialogConnect: React.FC = () => {
  const panelId = useSelector((store: TimeSeriesRootState) =>
    uiSelectors.getSourcePanelId(store, uiTypes.DialogTypes.TimeSeriesInfo),
  );
  const currentParameterInfoDisplayed = useSelector(
    (store: TimeSeriesRootState) =>
      timeSeriesSelectors.getParameterInfoParameter(store, panelId),
  );
  const relatedService =
    useSelector((store: TimeSeriesRootState) =>
      timeSeriesSelectors.getService(
        store,
        currentParameterInfoDisplayed?.serviceId || '',
      ),
    ) || ({ url: '' } as TimeSeriesService);

  const { data: collection, isFetching: isFetchingCollection } =
    useGetDetailsForCollection(
      relatedService,
      currentParameterInfoDisplayed?.collectionId || '',
    );

  const supportsInstances = !!collection?.dataQueries?.instances;
  const { data: instances, isFetching: isFetchingInstances } = useGetInstances(
    relatedService?.url,
    currentParameterInfoDisplayed?.collectionId || '',
    supportsInstances,
  );

  const { dialogOrder, onCloseDialog, setDialogOrder, uiSource, isDialogOpen } =
    useSetupDialog(uiTypes.DialogTypes.TimeSeriesInfo);

  if (!currentParameterInfoDisplayed) {
    return null;
  }

  return (
    <ParameterInfoDialog
      collectionInformation={collection}
      currentParameterInfo={currentParameterInfoDisplayed}
      instanceInformation={instances}
      isLoadingCollectionInformation={isFetchingCollection}
      isLoadingInstanceInformation={isFetchingInstances}
      isOpen={isDialogOpen}
      onClose={onCloseDialog}
      onMouseDown={setDialogOrder}
      order={dialogOrder}
      relatedService={relatedService}
      source={uiSource}
      supportsInstances={supportsInstances}
    />
  );
};
